/* Parapharmacy theme add-ons for Dawn. No dependencies. Loaded with defer. */
(function () {
  'use strict';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Hero slider (scroll-snap, dots, arrows, autoplay) ---------- */
  if (!customElements.get('hp-slider')) {
    class HpSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.hp-slider__track');
        this.slides = Array.from(this.querySelectorAll('.hp-slider__slide'));
        this.dots = Array.from(this.querySelectorAll('.hp-slider__dot'));
        if (!this.track || this.slides.length < 2) return;
        this.index = 0;
        this.querySelector('.hp-slider__arrow--prev')?.addEventListener('click', () => this.go(this.index - 1, true));
        this.querySelector('.hp-slider__arrow--next')?.addEventListener('click', () => this.go(this.index + 1, true));
        this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.index), true)));
        this.track.addEventListener('scroll', () => {
          window.clearTimeout(this.scrollTimer);
          this.scrollTimer = window.setTimeout(() => this.syncFromScroll(), 80);
        }, { passive: true });
        if (this.dataset.autoplay === 'true' && !reduceMotion) {
          this.delay = (Number(this.dataset.speed) || 5) * 1000;
          ['mouseenter', 'focusin', 'touchstart'].forEach((e) => this.addEventListener(e, () => this.stop(), { passive: true }));
          ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => this.play()));
          this.play();
        }
      }
      disconnectedCallback() { this.stop(); }
      play() { this.stop(); this.timer = window.setInterval(() => this.go(this.index + 1), this.delay); }
      stop() { window.clearInterval(this.timer); }
      go(i, user) {
        const n = this.slides.length;
        this.index = (i + n) % n;
        this.track.scrollTo({ left: this.slides[this.index].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
        this.updateDots();
        if (user) this.stop();
      }
      syncFromScroll() {
        const w = this.track.clientWidth || 1;
        this.index = Math.round(this.track.scrollLeft / w);
        this.updateDots();
      }
      updateDots() {
        this.dots.forEach((d, k) => (k === this.index ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
      }
    }
    customElements.define('hp-slider', HpSlider);
  }

  /* ---------- Generic horizontal carousel (products, categories, brands) ---------- */
  if (!customElements.get('hp-carousel')) {
    class HpCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.hp-carousel__track');
        this.buttons = Array.from(this.querySelectorAll(':scope > .hp-carousel__nav .hp-carousel__btn'));
        if (!this.track) return;
        this.buttons.forEach((btn) =>
          btn.addEventListener('click', () => {
            const dir = Number(btn.dataset.dir) || 1;
            this.track.scrollBy({ left: dir * this.track.clientWidth * 0.9, behavior: reduceMotion ? 'auto' : 'smooth' });
          })
        );
        this.update = this.update.bind(this);
        this.track.addEventListener('scroll', this.update, { passive: true });
        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(this.update);
          this.ro.observe(this.track);
        }
        this.update();
      }
      disconnectedCallback() { this.ro?.disconnect(); }
      update() {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        const overflow = max > 0;
        this.classList.toggle('hp-carousel--static', !overflow);
        if (this.buttons[0]) this.buttons[0].disabled = this.track.scrollLeft <= 2;
        if (this.buttons[1]) this.buttons[1].disabled = this.track.scrollLeft >= max;
      }
    }
    customElements.define('hp-carousel', HpCarousel);
  }

  /* ---------- Accessible tabs (WAI-ARIA tabs pattern) ---------- */
  if (!customElements.get('hp-tabs')) {
    class HpTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll(':scope .hp-tabs__list [role="tab"]')).filter((t) => t.closest('hp-tabs') === this);
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i, false));
          tab.addEventListener('keydown', (e) => {
            let next = null;
            if (e.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
            if (e.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
            if (e.key === 'Home') next = 0;
            if (e.key === 'End') next = this.tabs.length - 1;
            if (next !== null) { e.preventDefault(); this.select(next, true); }
          });
        });
      }
      select(i, focus) {
        this.tabs.forEach((tab, k) => {
          const active = k === i;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
          const panel = document.getElementById(tab.getAttribute('aria-controls'));
          if (panel) panel.hidden = !active;
          if (active && panel) panel.querySelectorAll('hp-carousel').forEach((c) => c.update && c.update());
        });
        if (focus) this.tabs[i].focus();
      }
    }
    customElements.define('hp-tabs', HpTabs);
  }

  /* ---------- Read more / read less ---------- */
  if (!customElements.get('hp-read-more')) {
    class HpReadMore extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('.hp-readmore__toggle');
        this.content = this.querySelector('.hp-readmore__content');
        if (!this.btn || !this.content) return;
        const limit = parseInt(getComputedStyle(this).getPropertyValue('--hp-collapsed-height'), 10) || 180;
        if (this.content.scrollHeight <= limit + 40) {
          this.classList.remove('is-collapsed');
          this.btn.hidden = true;
          return;
        }
        this.btn.addEventListener('click', () => {
          const collapsed = this.classList.toggle('is-collapsed');
          this.btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
          this.btn.textContent = collapsed ? this.btn.dataset.more : this.btn.dataset.less;
          if (collapsed) this.scrollIntoView({ block: 'nearest' });
        });
      }
    }
    customElements.define('hp-read-more', HpReadMore);
  }

  /* ---------- Sticky add to cart ---------- */
  if (!customElements.get('hp-sticky-atc')) {
    class HpStickyAtc extends HTMLElement {
      connectedCallback() {
        this.form = document.querySelector('product-info product-form form[data-type="add-to-cart-form"], product-form form[data-type="add-to-cart-form"]');
        this.submitBtn = this.form?.querySelector('[type="submit"]');
        if (!this.form || !this.submitBtn) return;
        this.hidden = false;
        this.querySelector('[data-sticky-submit]')?.addEventListener('click', () => {
          if (this.submitBtn.disabled) return;
          if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.submitBtn);
          else this.submitBtn.click();
        });
        // Scroll check (rAF-throttled): robust even when a fast scroll jumps past the button.
        let ticking = false;
        this.check = () => {
          ticking = false;
          const past = this.submitBtn.getBoundingClientRect().bottom < 0;
          this.classList.toggle('is-visible', past);
          document.body.classList.toggle('hp-sticky-atc-visible', past);
        };
        this.onScroll = () => { if (!ticking) { ticking = true; window.requestAnimationFrame(this.check); } };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.check();
        // Mirror price + availability when the variant changes (Dawn re-renders the main price).
        const mainPrice = document.querySelector('product-info [id^="price-"]');
        const stickyPrice = this.querySelector('[data-sticky-price]');
        const stickyBtn = this.querySelector('[data-sticky-submit]');
        if (mainPrice && stickyPrice) {
          this.mo = new MutationObserver(() => {
            stickyPrice.innerHTML = mainPrice.innerHTML;
            if (stickyBtn) {
              stickyBtn.disabled = this.submitBtn.disabled;
              const label = this.submitBtn.querySelector('span');
              if (label) stickyBtn.textContent = label.textContent.trim();
            }
          });
          this.mo.observe(mainPrice, { childList: true, subtree: true, characterData: true });
          if (this.submitBtn) this.mo.observe(this.submitBtn, { attributes: true, attributeFilter: ['disabled'] });
        }
      }
      disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); window.removeEventListener('resize', this.onScroll); this.mo?.disconnect(); document.body.classList.remove('hp-sticky-atc-visible'); }
    }
    customElements.define('hp-sticky-atc', HpStickyAtc);
  }

  /* ---------- Brands A–Z: letter filter + search ---------- */
  if (!customElements.get('hp-brands')) {
    class HpBrands extends HTMLElement {
      connectedCallback() {
        this.groups = Array.from(this.querySelectorAll('.hp-az__group'));
        this.letters = Array.from(this.querySelectorAll('button.hp-az__letter'));
        this.search = this.querySelector('[data-brand-search]');
        this.empty = this.querySelector('[data-brand-empty]');
        this.letter = 'all';
        this.letters.forEach((b) => b.addEventListener('click', () => {
          this.letter = b.dataset.letter;
          this.letters.forEach((x) => { x.classList.toggle('is-active', x === b); x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
          this.apply();
        }));
        this.search?.addEventListener('input', () => this.apply());
      }
      normalize(v) { return v.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-'); }
      apply() {
        const q = this.search ? this.normalize(this.search.value.trim()) : '';
        let shown = 0;
        this.groups.forEach((g) => {
          const letterOk = this.letter === 'all' || g.dataset.group === this.letter;
          let count = 0;
          g.querySelectorAll('.hp-az__item').forEach((item) => {
            const ok = letterOk && (!q || item.dataset.name.includes(q));
            item.hidden = !ok;
            if (ok) count++;
          });
          g.hidden = count === 0;
          shown += count;
        });
        if (this.empty) this.empty.hidden = shown !== 0;
      }
    }
    customElements.define('hp-brands', HpBrands);
  }

  /* ---------- Footer: columns open on desktop, accordions on mobile ---------- */
  if (!customElements.get('hp-footer-accordion')) {
    class HpFooterAccordion extends HTMLElement {
      connectedCallback() {
        this.mq = window.matchMedia('(min-width: 750px)');
        this.sync = () => this.querySelectorAll('details').forEach((d) => (this.mq.matches ? d.setAttribute('open', '') : d.removeAttribute('open')));
        this.mq.addEventListener('change', this.sync);
        this.querySelectorAll('summary').forEach((s) => s.addEventListener('click', (e) => { if (this.mq.matches) e.preventDefault(); }));
        this.sync();
      }
      disconnectedCallback() { this.mq?.removeEventListener('change', this.sync); }
    }
    customElements.define('hp-footer-accordion', HpFooterAccordion);
  }


  /* ---------- Accordion (FAQ): buttons + aria-expanded, single or multiple open ---------- */
  if (!customElements.get('hp-accordion')) {
    class HpAccordion extends HTMLElement {
      connectedCallback() {
        this.buttons = Array.from(this.querySelectorAll('.hp-faq__q[aria-controls]'));
        this.buttons.forEach((btn) => {
          const panel = document.getElementById(btn.getAttribute('aria-controls'));
          if (!panel) return;
          panel.hidden = btn.getAttribute('aria-expanded') !== 'true';
          btn.addEventListener('click', () => this.toggle(btn));
        });
      }
      toggle(btn) {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        if (open && this.dataset.single === 'true') this.buttons.forEach((b) => b !== btn && this.set(b, false));
        this.set(btn, open);
      }
      set(btn, open) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.hidden = !open;
      }
    }
    customElements.define('hp-accordion', HpAccordion);
  }
  /* ---------- UNIKA header: category bar toggle, mobile search, cart label ---------- */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-uh-toggle]');
    if (toggle) {
      const header = toggle.closest('[data-uh-header]');
      const target = document.getElementById(toggle.getAttribute('aria-controls'));
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (toggle.dataset.uhToggle === 'nav' && target) {
        target.toggleAttribute('data-closed', !open);
      }
      if (toggle.dataset.uhToggle === 'search' && header) {
        header.classList.toggle('is-search-open', open);
        if (open) header.querySelector('#Search-In-Header')?.focus();
      }
      return;
    }
    const cartLabel = event.target.closest('[data-uh-cart-label]');
    if (cartLabel && document.querySelector('cart-drawer')) {
      event.preventDefault();
      document.getElementById('cart-icon-bubble')?.click();
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const header = document.querySelector('[data-uh-header].is-search-open');
    if (!header) return;
    header.classList.remove('is-search-open');
    const btn = header.querySelector('[data-uh-toggle="search"]');
    btn?.setAttribute('aria-expanded', 'false');
    btn?.focus();
  });

  /* ---------- Mobile drawer accordion: release Dawn's panel focus trap, sync aria-expanded ---------- */
  document.addEventListener('click', (event) => {
    const summary = event.target.closest('.uh__drawer .menu-drawer__submenu summary, .uh__drawer .menu-drawer__menu > li > details > summary');
    if (!summary) return;
    window.setTimeout(() => {
      const details = summary.parentElement;
      summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(summary);
      const drawer = summary.closest('.menu-drawer');
      if (drawer && typeof window.trapFocus === 'function') window.trapFocus(drawer, summary);
    }, 180);
  });

  /* ---------- Collection mobile toolbar: sort (Dawn facets AJAX) + grid/list view ---------- */
  const VIEW_KEY = 'unika:collection-view';
  const applyView = (view) => {
    const container = document.getElementById('ProductGridContainer');
    const mode = view === 'list' ? 'list' : 'grid';
    if (container) {
      if (mode === 'list') container.setAttribute('data-view', 'list');
      else container.removeAttribute('data-view');
    }
    document.querySelectorAll('[data-ucol-view]').forEach((btn) => btn.setAttribute('aria-pressed', btn.dataset.ucolView === mode ? 'true' : 'false'));
  };
  let savedView = null;
  try { savedView = window.sessionStorage.getItem(VIEW_KEY); } catch (e) { savedView = null; }
  if (document.querySelector('[data-ucol-view]')) applyView(savedView);
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-ucol-view]');
    if (!btn) return;
    applyView(btn.dataset.ucolView);
    try { window.sessionStorage.setItem(VIEW_KEY, btn.dataset.ucolView); } catch (e) { /* storage unavailable */ }
  });
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-ucol-sort]');
    if (!select) return;
    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', select.value);
    params.delete('page');
    if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
      FacetFiltersForm.renderPage(params.toString());
    } else {
      window.location.search = params.toString();
    }
  });
})();
